import 'the-new-css-reset/css/reset.css'
import '../sass/style.scss'

import { FirebaseError, FirebaseOptions, initializeApp } from 'firebase/app'
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  User,
} from 'firebase/auth'
import {
  doc,
  getDoc,
  getFirestore,
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import * as Sentry from '@sentry/browser'
import { FirebaseErrorCode } from './enums/firebaseErrorCode'

/** プロジェクトの設定 */
interface ProjectConfig {
  /** Firebaseプロジェクトの設定 */
  firebase: FirebaseOptions
}

/** プロジェクトの設定 */
const config: ProjectConfig = makeProjectConfig()
/** Firebase App */
const app = initializeApp(config.firebase)
/** Firebase Auth */
const auth = getAuth(app)
/** Firebase Firestore */
const db = getFirestore(app)
/** 予期しないエラーが発生したときに表示するメッセージ */
const internalErrorMessage = '予期しないエラーが発生しました。'

/** 現在のユーザーを取得 */
let getCurrentUser: () => User | null

// Sentry の初期化
Sentry.init({
  dsn: 'https://1d893e0b089047543ad18850d45c8008@o985854.ingest.sentry.io/4506437094014976',
  tracesSampleRate: 0.1,
  tracePropagationTargets: [/^https:\/\/issueplusdesign\.jp\/university/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

(() => {
  /** 現在のユーザー */
  let currentUser: User | null = null
  /** 現在のユーザーを取得 */
  getCurrentUser = () => currentUser

  onAuthStateChanged(auth, (user) => {
    if (user) {
      currentUser = user
      document.body.classList.add('is-login')
      const date = new Date();
      date.setTime(date.getTime() + (1 * 24 * 60 * 60 * 1000)); // 現在時刻から1日後の時刻を設定
      document.cookie = `firebaseAuth=loggedIn; path=/; expires=${date.toUTCString()}`
      
      if (window.location.pathname.includes('mypage')) {
        getUserInfo()
      }
    } else {
      document.body.classList.remove('is-login')
      document.cookie = `firebaseAuth=; path=/; expires=${new Date(0).toUTCString()}`
      if (window.location.pathname === '/university/mypage/') {
        window.location.href = '/university/';
      }
    }
  })
})()

/**
 * エラーコードごとのエラーメッセージを返す
 * @param error FirebaseError
 * @returns エラーメッセージ
 */
const makeFirebaseErrorMessage = (error: FirebaseError): string => {
  switch (error.code) {
    case FirebaseErrorCode.EmailAlreadyInUse:
      return '入力されたメールアドレスはすでに使用されています。'
    case FirebaseErrorCode.InvalidEmail:
      return 'メールアドレスの形式が正しくありません。'
    case FirebaseErrorCode.InvalidPassword:
      return 'パスワードの形式が不正です。'
    case FirebaseErrorCode.UserNotFound:
      return 'ユーザーが見つかりません。'
    case FirebaseErrorCode.WeakPassWord:
      return 'パスワードは6文字以上の入力が必要です。'
    case FirebaseErrorCode.WrongPassword:
      return 'パスワードが違います。'
    default:
      return internalErrorMessage
  }
}

/**
 * エラーを表示する
 * @param error エラー
 */
const showError = (error: any) => {
  if (error instanceof FirebaseError) {
    alert(makeFirebaseErrorMessage(error))
  } else {
    Sentry.captureException(error)
    alert(internalErrorMessage)
  }
}

document.getElementById('login')?.addEventListener('click', () => {
  const email = document.querySelector<HTMLInputElement>('#email')?.value
  const password = document.querySelector<HTMLInputElement>('#password')?.value

  if (!(email && password)) {
    alert('未入力項目があります')
    return
  }

  signInWithEmailAndPassword(auth, email, password).then(async () => {
    const urlParams = new URLSearchParams(window.location.search)
    const redirectUrl = urlParams.get('redirectUrl')
    if (redirectUrl) {
      window.location.href = redirectUrl
    } else {
      const status = urlParams.get('status')
      window.location.href = status === 'registered' ? '/university/mypage' : '/university'
    }
  }).catch((error) => showError(error))
})

document.getElementById('logout')?.addEventListener('click', (event) => {
  event.preventDefault()
  if (!confirm('ログアウトしますか？')) {
    return
  }
    
  signOut(auth).then(() => {
    window.location.href = '/university'
  }).catch((error) => showError(error))
})

const getUserInfo = async () => {
  const uid = getCurrentUser()?.uid
  if (!uid) {
    throw new Error()
  }

  const docRef = doc(db, 'customers_info', uid)
  const docSnap = await getDoc(docRef)
  if (docSnap.exists()) {
    const userInfo = docSnap.data()
    const memberNameElement = document.getElementById('member-name');
    const memberEmailElement = document.getElementById('member-email');

    if (memberNameElement) {
      memberNameElement.innerHTML = `${userInfo.lastName} ${userInfo.firstName}さん`;
    }
    if (memberEmailElement) {
      memberEmailElement.innerHTML = userInfo?.email || '';
    }
  } else {
    console.log('No such document!');
  }
}

document.getElementById('reset-password')?.addEventListener('click', (event) => {
  event.preventDefault()
  const email = document.querySelector<HTMLInputElement>('#email')?.value
  const actionCodeSettings = {
    url: 'https://issueplusdesign.jp/university/login/',
  }
  if (!email) {
    alert('メールアドレスを入力してください')
    return
  }

  const sendResetPasswordEmail = httpsCallable(getFunctions(app, 'asia-northeast1'), 'sendResetPasswordEmail');
  sendResetPasswordEmail({ email, actionCodeSettings }).then(() => {
    alert('メールを送信しました。メール内のリンクをクリックしてパスワードを再設定してください。')
    window.location.href = '/university'
  }).catch((error) => showError(error))
})

/**
 * Firebase, Stripe の設定を本番とそれ以外で使い分ける
 */
function makeProjectConfig(): ProjectConfig {
  if (window.location.href.includes('issueplusdesign.jp')) {
    return {
      firebase: {
        apiKey: 'AIzaSyAw3j8sJVYLiq6uETfRB247VFyPml8Z0HY',
        authDomain: 'iplusduniversity.firebaseapp.com',
        projectId: 'iplusduniversity',
        storageBucket: 'iplusduniversity.appspot.com',
        messagingSenderId: '995165709770',
        appId: '1:995165709770:web:05e865cce5901545d89b33',
        measurementId: 'G-45SYZ2LKS4'
      },
    }
  } else {
    return {
      firebase: {
        apiKey: 'AIzaSyAc05OTVXkG_3-Mh2NXmbXkyb5AEkV1MxI',
        authDomain: 'test-stripe-b77fa.firebaseapp.com',
        projectId: 'test-stripe-b77fa',
        storageBucket: 'test-stripe-b77fa.appspot.com',
        messagingSenderId: '755618483032',
        appId: '1:755618483032:web:65da946dc67a7ca3aff32d'
      },
    }
  }
}